// Visão geral.
//
// OS NÚMEROS VÊM DAS ROTAS QUE JÁ EXISTEM, e não de uma rota de painel.
//
// Isso é escolha, não preguiça: cada uma dessas rotas já aplica o escopo de
// quem pediu. Uma rota de resumo teria que reimplementar esse filtro, e seria
// mais um lugar onde a fronteira entre clientes pode estar errada — o lugar
// mais fácil de errar, porque um número agregado não denuncia de quem ele é.
//
// O custo são seis requisições em paralelo, cada uma pedindo UMA linha só
// para ler o total do envelope de paginação.

import { useQueries, useQuery } from "@tanstack/react-query";

import { auditoria, caixas, dominios, entregas, quarentena, revendas } from "../api/admin";
import { api } from "../api/cliente";
import { useSessao } from "../sessao/contexto";
import { dataParaCampo } from "../ui/formato";
import { Cabecalho, Indicador } from "./comuns";

const SO_O_TOTAL = { por_pagina: 1 };

function hojeISO(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}



export function VisaoGeral() {
  const { podeVer } = useSessao();

  const [
    qCaixas,
    qDominios,
    qRevendas,
    qQuarentena,
    qFila,
    qEntregues,
    qRejeitados,
  ] = useQueries({
    queries: [
      { queryKey: ["p", "caixas"], queryFn: () => caixas.listar(SO_O_TOTAL) },
      { queryKey: ["p", "dominios"], queryFn: () => dominios.listar(SO_O_TOTAL) },
      {
        queryKey: ["p", "revendas"],
        queryFn: () => revendas.listar(SO_O_TOTAL),
        enabled: podeVer("super_admin"),
      },
      { queryKey: ["p", "quarentena"], queryFn: () => quarentena.listar(SO_O_TOTAL) },
      { queryKey: ["p", "fila"], queryFn: () => entregas.fila(SO_O_TOTAL) },
      {
        queryKey: ["p", "entregues"],
        queryFn: () => entregas.listar({ ...SO_O_TOTAL, tipo: "entregue", desde: hojeISO() }),
      },
      {
        queryKey: ["p", "rejeitados"],
        queryFn: () => entregas.listar({ ...SO_O_TOTAL, tipo: "rejeitado", desde: hojeISO() }),
      },
    ],
  });

  const saude = useQuery({
    queryKey: ["p", "saude"],
    queryFn: () => api.get<{ estado: string; componentes: Record<string, { estado: string }> }>("/saude"),
    // O estado dos serviços é o que muda mais rápido nesta tela.
    refetchInterval: 30_000,
  });

  const qAuditoria = useQuery({
    queryKey: ["p", "auditoria-negadas"],
    queryFn: () => auditoria({ ...SO_O_TOTAL, acao: "" }),
  });

  const total = (q: { data?: { total?: number } }) => q.data?.total ?? "—";
  // A MESMA "hoje" que os cartões contaram, no formato que a tela de
  // entregas fala. O cartão conta com `desde=<ISO completo>` e a lista
  // filtra por `<input type="date">`: mandar o ISO inteiro no endereço
  // deixaria o campo de data VAZIO no destino — filtro valendo e tela
  // dizendo que não há filtro.
  const hoje = dataParaCampo(new Date());
  const entregues = qEntregues.data?.total ?? 0;
  const rejeitados = qRejeitados.data?.total ?? 0;
  const movimento = entregues + rejeitados;

  return (
    <>
      <Cabecalho
        titulo="Visão geral"
        descricao="O que você alcança, pelo seu nível de acesso."
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Indicador
          rotulo="Caixas"
          valor={total(qCaixas)}
          detalhe={`${total(qDominios)} domínio(s)`}
          para="/painel/caixas"
        />
        {podeVer("super_admin") && (
          <Indicador rotulo="Revendas" valor={total(qRevendas)} para="/painel/revendas" />
        )}
        <Indicador
          rotulo="Entregues hoje"
          valor={entregues}
          detalhe={
            movimento > 0
              ? `${Math.round((entregues / movimento) * 100)}% do que foi processado`
              : "nenhum movimento hoje"
          }
          // O MESMO filtro que contou: tipo e data vão no endereço, então a
          // lista abre mostrando exatamente as linhas deste número.
          para={`/painel/entregas?tipo=entregue&desde=${hoje}`}
        />
        <Indicador
          rotulo="Recusadas hoje"
          valor={rejeitados}
          detalhe="antes de entrar, pelo antispam ou pelas regras"
          para={`/painel/entregas?tipo=rejeitado&desde=${hoje}`}
        />
        <Indicador
          rotulo="Em quarentena"
          valor={total(qQuarentena)}
          detalhe="aguardando o usuário decidir"
          alerta={(qQuarentena.data?.total ?? 0) > 0}
          para="/painel/quarentena"
        />
        <Indicador
          rotulo="Presas na fila"
          valor={total(qFila)}
          detalhe="ainda tentando sair"
          alerta={(qFila.data?.total ?? 0) > 0}
          // A fila VIVE na tela de entregas, com indicador próprio no topo.
          // `tipo=adiado` é o que ela mostra em lista: o que não conseguiu
          // sair ainda está sendo adiado.
          para={`/painel/entregas?tipo=adiado&desde=${hoje}`}
        />
        <Indicador
          rotulo="Registros de auditoria"
          valor={total(qAuditoria)}
          detalhe="tudo que foi feito na plataforma"
          para="/painel/auditoria"
        />
        <Indicador
          rotulo="Estado do serviço"
          valor={saude.data?.estado === "ok" ? "Funcionando" : (saude.data?.estado ?? "…")}
          detalhe={saude.isError ? "a API não respondeu" : "verificado a cada 30 s"}
          alerta={saude.isError || (saude.data?.estado !== undefined && saude.data.estado !== "ok")}
        />
      </div>

      {saude.data?.componentes && (
        <section className="mt-6 rounded-xl border border-linha p-4">
          <h2 className="mb-3 text-sm font-medium text-tinta">Componentes</h2>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(saude.data.componentes).map(([nome, c]) => (
              <div key={nome} className="flex items-center gap-2 rounded-lg bg-fundo px-3 py-2 text-sm">
                <span
                  className={`h-2 w-2 flex-shrink-0 rounded-full ${
                    c.estado === "ok" ? "bg-acao" : "bg-perigo"
                  }`}
                />
                <span className="capitalize">{nome}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-tinta-fraca">
            A API reporta o que ela mesma alcança. Postfix, Dovecot, Rspamd e ClamAV
            têm verificação própria no Docker — ainda não exposta aqui.
          </p>
        </section>
      )}
    </>
  );
}
